import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

/**
 * Shallow-compare two objects by their top-level entries.
 * @param {object} a - First object.
 * @param {object} b - Second object.
 * @returns {boolean} Whether the objects are shallowly equal.
 */
const shallowEqual = (a = {}, b = {}) => {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
};

/**
 * useBuyerManuallyEdited hook.
 * Track whether the user has manually edited the purchaser ("comprador") form in
 * QuickCheckout. The buyer is normally mirrored from Passenger 1 (CON-962); once the
 * user edits the buyer by hand, that edit must be respected and the passenger→buyer
 * sync must stop overwriting it.
 *
 * Real user edits are distinguished from the programmatic sync via redux-form's
 * `active` field: `syncPurchaserFromFirstPassenger` dispatches `change('purchaser', ...)`
 * while the focus sits on the passengers form (or nowhere), so `state.form.purchaser.active`
 * is null; a genuine buyer edit happens with a purchaser field focused. `initialize`
 * (pre-fill) also runs with no purchaser field active, so it never trips the latch.
 * @returns {boolean} Whether the buyer was manually edited (latched true once set).
 */
export default function useBuyerManuallyEdited() {
  const activeField = useSelector((state) => state.form?.purchaser?.active || null);
  const values = useSelector((state) => state.form?.purchaser?.values);
  const [edited, setEdited] = useState(false);
  const prevValuesRef = useRef(values);

  useEffect(() => {
    if (!edited && activeField && !shallowEqual(values, prevValuesRef.current)) {
      setEdited(true);
    }
    prevValuesRef.current = values;
  }, [values, activeField, edited]);

  return edited;
}
